import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import {
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Card,
  Pagination,
  Button,
  Box,
  Typography,
  Chip,
  Alert,
  Avatar,
  Menu,
  IconButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Collapse,
  Divider,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import TuneIcon from "@mui/icons-material/Tune";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import HistoryIcon from "@mui/icons-material/History";
import TimelineIcon from "@mui/icons-material/Timeline";
import ScienceIcon from "@mui/icons-material/Science";
import ReceiptIcon from "@mui/icons-material/Receipt";
import ListAltIcon from "@mui/icons-material/ListAlt";
import DescriptionIcon from "@mui/icons-material/Description";
import AssignmentIcon from "@mui/icons-material/Assignment";
import ChildCareIcon from "@mui/icons-material/ChildCare";
import EventNoteIcon from "@mui/icons-material/EventNote";
import PrintIcon from "@mui/icons-material/Print";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import MDBox from "components/MDBox";
import irmLogo from 'assets/images/logoIRM.png';
import printPatientCard from 'utils/printPatientCard';
import MDTypography from "components/MDTypography";
import LoaderOverlay from "components/LoaderOverlay";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import FollowUpDialog from "components/FollowUpDialog";
import ChangeDoctorDialog from "components/ChangeDoctorDialog";
import MDDatePicker from "components/MDDatePicker";
import { usePermissions } from "hooks/usePermissions";
import { api } from "../../api";

const selectBoxHeight = 45;

// Utility function to calculate age from date of birth
const calculateAge = (dateOfBirth) => {
  if (!dateOfBirth) return null;
  
  const today = new Date();
  const birthDate = new Date(dateOfBirth);
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  
  return age;
};

// Utility function to check if patient is a minor (under 18)
const isMinor = (dateOfBirth) => {
  const age = calculateAge(dateOfBirth);
  return age !== null && age < 18;
};

const defaultSearchValues = {
  gender: "F", // Default to Female
  search_text: "",
  first_name: "",
  last_name: "",
  country_id: "",
  created_from: "",
  created_to: "",
  doctor_id: "",
  page: 1,
  limit: 10,
};

function PatientList() {
  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: defaultSearchValues,
  });

  // Theme hook
  const theme = useTheme();
  const logoColor = theme.palette.logo.main;
  const logoState = theme.palette.logo.state;

  // Navigation hook
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();

  // State management
  const [patients, setPatients] = useState([]);
  const [countries, setCountries] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    pages: 0,
  });
  const [error, setError] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);

  // Follow-up dialog state
  const [followUpDialogOpen, setFollowUpDialogOpen] = useState(false);
  const [followUpContactId, setFollowUpContactId] = useState(null);
  const [followUpDoctors, setFollowUpDoctors] = useState([]);
  const [followUpDefaultDoctor, setFollowUpDefaultDoctor] = useState(null);

  // Change doctor dialog state
  const [changeDoctorDialogOpen, setChangeDoctorDialogOpen] = useState(false);
  const [changeDoctorPatient, setChangeDoctorPatient] = useState(null);

  const { hasPermission } = usePermissions();
  const canChangeDoctor = hasPermission('v1-contacts-change-doctor');
  const canViewAuditLog = hasPermission('v1-contact-audit-logs');

  const currentUserId = (() => {
    try {
      const raw = localStorage.getItem('user') || localStorage.getItem('userData');
      if (raw) {
        const parsed = JSON.parse(raw);
        return parsed?.user_id || parsed?.id || 1;
      }
    } catch { /* ignore */ }
    return 1;
  })();

  // Load countries on component mount
  useEffect(() => {
    const loadCountries = async () => {
      try {
        const response = await api.master.getActiveCountries();
        if (response?.data?.countries?.length) {
          setCountries(response.data.countries);

          // Find and set default country where is_default is 'Y'
          const defaultCountry = response.data.countries.find(
            (country) => country.is_default === "Y" || country.is_default === true
          );
          if (defaultCountry) {
            setValue("country_id", defaultCountry.id);
          }
        }
      } catch (error) {
        console.error("Error loading countries:", error);
        setError("Failed to load countries");
      }
    };

    loadCountries();
  }, [setValue, setError]);

  // Load consultants (doctors) for the Advanced Search filter
  useEffect(() => {
    api.contacts.getDoctorsList()
      .then((res) => setConsultants(res?.data || []))
      .catch(() => setConsultants([]));
  }, []);

  // Reflect the active search in the URL so it survives navigating away and
  // coming back (e.g. browser back from a patient's details page).
  const updateUrlFromSearch = (data) => {
    const params = new URLSearchParams();
    if (data.gender) params.set("gender", data.gender);
    if (data.country_id) params.set("country_id", data.country_id);
    if (data.search_text?.trim()) params.set("search_text", data.search_text.trim());
    if (data.first_name?.trim()) params.set("first_name", data.first_name.trim());
    if (data.last_name?.trim()) params.set("last_name", data.last_name.trim());
    if (data.created_from) params.set("created_from", data.created_from);
    if (data.created_to) params.set("created_to", data.created_to);
    if (data.doctor_id) params.set("doctor_id", data.doctor_id);
    params.set("page", String(data.page || 1));
    params.set("limit", String(data.limit || 10));
    setSearchParams(params, { replace: true });
  };

  const onSearch = async (data) => {
    // Validate that at least gender and country are provided (mandatory fields)
    if (!data.gender || !data.country_id) {
      setError("Gender and Country are required fields");
      return;
    }

    setLoading(true);
    setError("");
    try {
      // Prepare search parameters
      const searchParams = {
        persona_slug: "patient", // Required parameter
        gender: data.gender,
        country_id: data.country_id,
        page: data.page || 1,
        limit: data.limit || 10,
      };

      // Add optional search text if provided
      if (data.search_text && data.search_text.trim()) {
        searchParams.search_text = data.search_text.trim();
      }

      // Add optional first_name if provided
      if (data.first_name && data.first_name.trim()) {
        searchParams.first_name = data.first_name.trim();
      }

      // Add optional last_name if provided
      if (data.last_name && data.last_name.trim()) {
        searchParams.last_name = data.last_name.trim();
      }

      // Add optional created-date range if provided
      if (data.created_from) {
        searchParams.created_from = data.created_from;
      }
      if (data.created_to) {
        searchParams.created_to = data.created_to;
      }

      // Add optional consultant (doctor) filter if provided
      if (data.doctor_id) {
        searchParams.doctor_id = data.doctor_id;
      }

      updateUrlFromSearch(data);

      const response = await api.contacts.searchContacts(searchParams);
      if (response?.data) {
        setPatients(response.data.contacts || []);
        setPagination({
          total: response.data.pagination?.total || 0,
          page: response.data.pagination?.page || 1,
          limit: response.data.pagination?.limit || 10,
          pages: response.data.pagination?.pages || 0,
        });
        setSearchPerformed(true);
      }
    } catch (error) {
      console.error("Error searching patients:", error);
      setError(error.response?.data?.message || "Failed to search patients");
      setPatients([]);
      setPagination({ total: 0, page: 1, limit: 10, pages: 0 });
    } finally {
      setLoading(false);
    }
  };

  // Any manual search submission (quick or advanced fields) starts fresh from
  // page 1 — only explicit pagination clicks should move to another page.
  const handleSearchSubmit = (data) => {
    setValue("page", 1);
    onSearch({ ...data, page: 1 });
  };

  const handlePageChange = (event, newPage) => {
    const currentFormData = watch();
    setValue("page", newPage);
    onSearch({ ...currentFormData, page: newPage });
  };

  const handleClearSearch = () => {
    reset(defaultSearchValues);
    setPatients([]);
    setSearchPerformed(false);
    setPagination({ total: 0, page: 1, limit: 10, pages: 0 });
    setError("");
    setAdvancedOpen(false);
    setSearchParams({}, { replace: true });
  };

  // Restore a previous search from the URL (e.g. after navigating back from
  // a patient's details page) — runs once on mount.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (!params.toString()) return;

    const restored = {
      ...defaultSearchValues,
      gender: params.get("gender") || defaultSearchValues.gender,
      country_id: params.get("country_id") || "",
      search_text: params.get("search_text") || "",
      first_name: params.get("first_name") || "",
      last_name: params.get("last_name") || "",
      created_from: params.get("created_from") || "",
      created_to: params.get("created_to") || "",
      doctor_id: params.get("doctor_id") || "",
      page: parseInt(params.get("page") || "1", 10),
      limit: parseInt(params.get("limit") || "10", 10),
    };

    reset(restored);
    if (restored.created_from || restored.created_to || restored.doctor_id) {
      setAdvancedOpen(true);
    }
    if (restored.gender && restored.country_id) {
      onSearch(restored);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getGenderDisplay = (gender) => {
    switch (gender) {
      case "M":
        return "Male";
      case "F":
        return "Female";
      case "O":
        return "Other";
      case "U":
        return "Unspecified";
      default:
        return "N/A";
    }
  };
  const getGenderColor = (gender) => {
    switch (gender) {
      case "M":
        return "success";
      case "F":
        return "info";
      case "O":
        return "warning";
      case "U":
        return "default";
      default:
        return "default";
    }
  };

  // Format date to display in a user-friendly way
  const formatDate = (dateString) => {
    if (!dateString) return "Unknown";
    
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString; // Return original if invalid
    
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric"
    });
  };
  
  // Calculate age from date of birth
  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return null;
    
    const birthDate = new Date(dateOfBirth);
    if (isNaN(birthDate.getTime())) return null; // Invalid date
    
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDifference = today.getMonth() - birthDate.getMonth();
    
    if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age;
  };

  // Check if patient is a minor (under 18 years old)
  const isMinor = (dateOfBirth) => {
    const age = calculateAge(dateOfBirth);
    return age !== null && age < 18;
  };

  // Get patient initials for avatar
  const getPatientInitials = (name) => {
    if (!name) return "P";
    const names = name.trim().split(" ");
    if (names.length >= 2) {
      return (names[0][0] + names[1][0]).toUpperCase();
    }
    return names[0][0].toUpperCase();
  };

  // Get avatar background color based on gender
  const getAvatarColor = (gender) => {
    switch (gender) {
      case "M":
        return "#1976d2"; // Blue for male
      case "F":
        return "#e91e63"; // Pink for female
      case "O":
        return "#ff9800"; // Orange for other
      case "U":
        return "#757575"; // Gray for unspecified
      default:
        return "#9e9e9e"; // Default gray
    }
  };

  // Handle view patient details
  const handleViewDetails = (patientCode, coupleCode) => {
    console.log("Viewing details for:", { patientCode, coupleCode });

    // Close the menu
    handleMenuClose();

    // Navigate to patient details page
    navigate(`/patient-details/${patientCode}/${coupleCode || "undefined"}`);
  };

  // Handle edit patient
  const handleEditPatient = (patientCode, coupleCode) => {
    console.log("Editing patient:", { patientCode, coupleCode });

    // Close the menu
    handleMenuClose();

    // Navigate to patient edit page
    navigate(`/patient-edit/${patientCode}/${coupleCode || "undefined"}`);
  };

  // Handle view patient history
  const handleViewHistory = (patientCode, coupleCode) => {
    console.log("Viewing history for:", { patientCode, coupleCode });

    // Close the menu
    handleMenuClose();

    // Navigate to patient history page
    navigate(`/contact-histories/${patientCode}/${coupleCode || "undefined"}`);
  };

  // Handle view patient audit log (change history)
  const handleViewAuditLog = (patientCode) => {
    handleMenuClose();
    navigate(`/audit-log/${patientCode}`);
  };

  // Handle view patient investigations
  const handleViewInvestigations = (patientCode, coupleCode) => {
    console.log("Viewing investigations for:", { patientCode, coupleCode });

    // Close the menu
    handleMenuClose();

    // Navigate to patient investigations page
    navigate(`/contact-investigations/${patientCode}/${coupleCode || "undefined"}`);
  };

  // Handle create draft prescription
  const handleCreateDraftPrescription = (patientCode, coupleCode) => {
    console.log("Creating draft prescription for:", { patientCode, coupleCode });

    // Close the menu
    handleMenuClose();

    // Navigate to create prescription page
    navigate(`/prescription/create/draft/${patientCode}/${coupleCode || "undefined"}`);
  };

  // Handle view patient prescriptions
  const handleViewPrescriptions = (patientCode, coupleCode) => {
    console.log("Viewing prescriptions for:", { patientCode, coupleCode });

    // Close the menu
    handleMenuClose();

    // Navigate to patient prescription search page
    navigate(`/prescription/patient/${patientCode}/${coupleCode || "undefined"}`);
  };

  // Handle view patient documents
  const handleViewDocuments = (patientCode, coupleCode) => {
    console.log("Viewing documents for:", { patientCode, coupleCode });
    
    // Close the menu
    handleMenuClose();
    
    // Navigate to patient documents page
    navigate(`/patient-documents/${patientCode}/${coupleCode || "undefined"}`);
  };
  
  // Handle view infertility summary
  const handleViewInfertilitySummary = (patientCode, coupleCode) => {
    console.log("Viewing infertility summary for:", { patientCode, coupleCode });

    // Close the menu
    handleMenuClose();
    
    // Navigate to infertility summary page
    navigate(`/infertility-summary/${patientCode}/${coupleCode || "undefined"}`);
    // Navigate to infertility summary page
    navigate(`/infertility-summary/${patientCode}/${coupleCode || "undefined"}`);
  };

  // Handle create follow-up from patient list
  const handleCreateFollowUp = async (patient) => {
    handleMenuClose();
    const contactId = patient?.contact?.id || patient?.id;
    setFollowUpContactId(contactId);

    // Load doctors and default doctor
    let doctorList = [];
    let defaultDr = null;
    try {
      const usersResp = api.user.getActiveUsers ? await api.user.getActiveUsers() : null;
      if (usersResp?.data?.data) {
        doctorList = usersResp.data.data.map(u => ({ id: u.id, name: u.name || u.full_name || `User #${u.id}` }));
      }
    } catch { /* ignore */ }

    if (contactId) {
      try {
        const drResp = await api.opd.getLastDoctorForContact(contactId);
        defaultDr = drResp?.data?.data?.doctor_id || null;
      } catch { /* ignore */ }
    }

    // Fallback: use patient's primary doctor info
    if (!defaultDr && patient?.doctor_info?.doctor_id) {
      defaultDr = patient.doctor_info.doctor_id;
    }

    if (doctorList.length === 0 && defaultDr) {
      doctorList = [{ id: defaultDr, name: patient?.doctor_info?.doctor_name || `Doctor #${defaultDr}` }];
    }

    setFollowUpDoctors(doctorList);
    setFollowUpDefaultDoctor(defaultDr);
    setFollowUpDialogOpen(true);
  };

  // Handle open change-doctor dialog for a patient row
  const handleOpenChangeDoctor = (patient) => {
    setChangeDoctorPatient(patient);
    setChangeDoctorDialogOpen(true);
  };

  // Refresh current search results after a successful doctor change
  const handleDoctorChanged = () => {
    onSearch(watch());
  };

  const handlePrintCard = (patient) => {
    handleMenuClose();
    const TEAL = '#00798E';
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    const issuedBy = u.name || u.user_name || '';
    const issuedAt = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    const patientId = patient?.external_code || '';
    const coupleId = patient?.couple_code || '';
    const patientName = patient?.name || '';
    const patientAge = patient?.contact?.date_of_birth
      ? calculateAge(patient.contact.date_of_birth)
      : (patient?.contact?.age || '');
    const patientGender = patient?.contact?.gender || '';
    const patientMobile = patient?.phone_number || '';
    const spouseExtCode = patient?.spouse?.contact?.external_code || '';
    const spouseName = patient?.spouse?.contact
      ? [patient.spouse.contact.first_name, patient.spouse.contact.last_name].filter(Boolean).join(' ')
      : '';
    const spouseAge = patient?.spouse?.contact?.date_of_birth
      ? calculateAge(patient.spouse.contact.date_of_birth)
      : (patient?.spouse?.contact?.age || '');

    const barcodeScript = [
      '<script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"></script>',
      '<script>',
      'window.onload=function(){',
      `if(document.getElementById('bc-patient'))JsBarcode('#bc-patient','${patientId}',{format:'CODE128',width:1.2,height:28,displayValue:true,fontSize:8,margin:1});`,
      `if(document.getElementById('bc-couple'))JsBarcode('#bc-couple','${coupleId}',{format:'CODE128',width:1.2,height:28,displayValue:true,fontSize:8,margin:1});`,
      spouseExtCode ? `if(document.getElementById('bc-spouse'))JsBarcode('#bc-spouse','${spouseExtCode}',{format:'CODE128',width:1.2,height:28,displayValue:true,fontSize:8,margin:1});` : '',
      '};',
      '</script>',
    ].join('\n');

    // A6 landscape: left side = patient info, right side = barcodes stacked vertically
    const genderLabel = patientGender === 'M' ? 'Male' : patientGender === 'F' ? 'Female' : 'Other';
    const html = `<!DOCTYPE html>
<html><head><meta charset="UTF-8"/>
<title>ID Card — ${patientId}</title>
<style>
  @page{size:148mm 105mm landscape;margin:4mm}
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:Arial,sans-serif;width:140mm}
  @media print{#ptb{display:none!important}body{padding-top:0!important}}
  .card{border:2px solid ${TEAL};border-radius:5px;padding:3mm;display:flex;flex-direction:column;gap:2mm;height:97mm}
  .card-header{display:flex;align-items:center;gap:3mm;border-bottom:1.5px solid ${TEAL};padding-bottom:2mm}
  .card-header img{height:28px}
  .card-header-text{flex:1}
  .card-header-text .title{font-size:9pt;font-weight:bold;color:${TEAL}}
  .card-header-text .sub{font-size:7pt;color:#555}
  .card-body{display:flex;gap:3mm;flex:1}
  .info-side{flex:1;display:flex;flex-direction:column;gap:1.5mm}
  .barcode-side{width:68mm;display:flex;flex-direction:column;gap:2mm;border-left:1px dashed #ccc;padding-left:3mm;justify-content:center}
  .info-row{font-size:7.5pt}
  .info-row .label{color:#666;font-size:6pt;display:block}
  .info-row .value{font-weight:700;color:#111}
  .bc-row{text-align:center}
  .bc-label{font-size:6pt;color:${TEAL};font-weight:700;margin-bottom:0.5mm}
  .bc-row svg{max-width:100%}
  .card-footer{display:flex;justify-content:space-between;font-size:6pt;color:#666;border-top:1px solid #ddd;padding-top:1.5mm}
</style></head>
<body style="padding:42px 0 0 0">
<div id="ptb" style="position:fixed;top:0;left:0;right:0;background:${TEAL};color:#fff;padding:7px 14px;display:flex;justify-content:space-between;align-items:center;z-index:9999;font-family:Arial,sans-serif">
  <span style="font-weight:700;font-size:9.5pt">ID Card — ${patientId}</span>
  <div style="display:flex;gap:8px">
    <button onclick="window.print()" style="background:#fff;color:${TEAL};border:none;padding:5px 13px;border-radius:4px;font-weight:700;cursor:pointer;font-size:8.5pt">&#128438; Print</button>
    <button onclick="window.close()" style="background:transparent;color:rgba(255,255,255,0.85);border:1px solid rgba(255,255,255,0.4);padding:5px 9px;border-radius:4px;font-weight:700;cursor:pointer;font-size:8.5pt">&#10005;</button>
  </div>
</div>
<div class="card">
  <div class="card-header">
    <img src="${irmLogo}" alt="IRM Logo"/>
    <div class="card-header-text">
      <div class="title">IRM Fertility Centre — Patient Identity Card</div>
      <div class="sub">Confidential — For Patient Use Only</div>
    </div>
  </div>
  <div class="card-body">
    <div class="info-side">
      <div class="info-row"><span class="label">Patient Name</span><span class="value">${patientName}</span></div>
      <div class="info-row"><span class="label">Age / Gender</span><span class="value">${patientAge || '—'} Yrs / ${genderLabel}</span></div>
      <div class="info-row"><span class="label">Mobile</span><span class="value">${patientMobile || '—'}</span></div>
      <div class="info-row"><span class="label">Patient ID</span><span class="value" style="color:${TEAL};font-size:8pt">${patientId}</span></div>
      <div class="info-row"><span class="label">Couple ID</span><span class="value" style="color:${TEAL};font-size:8pt">${coupleId}</span></div>
      ${spouseName ? `<div class="info-row"><span class="label">Spouse</span><span class="value">${spouseName}${spouseAge ? ' · ' + spouseAge + ' Yrs' : ''}</span></div>` : ''}
    </div>
    <div class="barcode-side">
      ${patientId ? `<div class="bc-row"><div class="bc-label">Patient ID</div><svg id="bc-patient"></svg></div>` : ''}
      ${coupleId ? `<div class="bc-row"><div class="bc-label">Couple ID</div><svg id="bc-couple"></svg></div>` : ''}
      ${spouseExtCode ? `<div class="bc-row"><div class="bc-label">Spouse ID</div><svg id="bc-spouse"></svg></div>` : ''}
    </div>
  </div>
  <div class="card-footer">
    <span>Issued At: ${issuedAt}</span>
    <span>Issued By: ${issuedBy}</span>
  </div>
</div>
${barcodeScript}
</body></html>`;

    const win = window.open('', '_blank', 'width=700,height=500,toolbar=0,menubar=0');
    win.document.write(html);
    win.document.close();
  };

  // Same PVC-style identity card shown on Old Patient Registration success
  const handlePrintPatientCard = (patient) => {
    handleMenuClose();
    const patientAge = patient?.contact?.date_of_birth
      ? calculateAge(patient.contact.date_of_birth)
      : (patient?.contact?.age || '');
    const spouseAge = patient?.spouse?.contact?.date_of_birth
      ? calculateAge(patient.spouse.contact.date_of_birth)
      : (patient?.spouse?.contact?.age || '');

    printPatientCard({
      patientExternalCode: patient?.external_code || '',
      coupleExternalCode: patient?.couple_code || '',
      spouseExternalCode: patient?.spouse?.contact?.external_code || '',
      first_name: patient?.contact?.first_name || '',
      last_name: patient?.contact?.last_name || '',
      age: patientAge,
      gender: patient?.contact?.gender || '',
      mobile: patient?.phone_number || '',
      spouse_first_name: patient?.spouse?.contact?.first_name || '',
      spouse_last_name: patient?.spouse?.contact?.last_name || '',
      spouse_age: spouseAge,
      spouse_gender: patient?.spouse?.contact?.gender || '',
    });
  };

  // Handle menu open
  const handleMenuOpen = (event, patient) => {
    setAnchorEl(event.currentTarget);
    setSelectedPatient(patient);
  };

  // Handle menu close
  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedPatient(null);
  };

  // Prepare table data for DataTable component
  const getTableData = () => {
    const columns = [
      { Header: "Profile", accessor: "profile", align: "center" },
      { Header: "Patient Code", accessor: "patient_code", align: "left" },
      { Header: "Name", accessor: "name", align: "left" },
      { Header: "Registered At", accessor: "created_date", align: "center" },
      { Header: "Gender", accessor: "gender", align: "center" },
      { Header: "Phone", accessor: "phone", align: "left" },
      { Header: "Doctor", accessor: "doctor", align: "left" },
      { Header: "Couple Code", accessor: "couple_code", align: "left" },
      { Header: "Actions", accessor: "actions", align: "center", sticky: "right" },
    ];

    const rows = patients.map((patient) => {
      // Check if patient is a minor based on date of birth
      const isPatientMinor = isMinor(patient.contact.date_of_birth);
      
      // Define background color for minor patients
      const rowStyle = isPatientMinor 
        ? { backgroundColor: "#fff3e0" } // Light orange/amber background for minors
        : {};
        
      return {
        profile: (
          <Box display="flex" justifyContent="center">
            <Avatar
              src={
                patient.contact.profile_picture_base_url && patient.contact.profile_picture_path
                  ? patient.contact.profile_picture_base_url + patient.contact.profile_picture_path
                  : ""
              }
              alt={patient.name || "Patient"}
              sx={{
                width: 60,
                height: 60,
                bgcolor: getAvatarColor(patient.contact.gender),
                border: isPatientMinor ? "3px solid #ff9800" : "2px solid #e0e0e0", // Highlight avatar border for minors
                fontSize: "1.2rem",
                fontWeight: "bold",
              }}
            >
              {(!patient.contact.profile_picture_base_url || !patient.contact.profile_picture_path) &&
                getPatientInitials(patient.name)}
            </Avatar>
          </Box>
        ),
        patient_code: (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <MDTypography variant="caption" color="text" fontWeight="medium" sx={rowStyle}>
              {patient.external_code || "N/A"}
            </MDTypography>
            {isPatientMinor && (
              <Tooltip title={`Minor Patient - Age: ${calculateAge(patient.contact.date_of_birth)} years - DOB: ${formatDate(patient.contact.date_of_birth)}`}>
                <Chip 
                  label="Minor" 
                  size="small" 
                  color="warning" 
                  sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} 
                />
              </Tooltip>
            )}
          </Box>
        ),
        name: (
          <MDTypography 
            variant="caption" 
            color="text" 
            fontWeight={isPatientMinor ? "bold" : "regular"}
            sx={rowStyle}
          >
            {patient.name || "N/A"}
          </MDTypography>
        ),
        gender: (
          <Chip
            label={getGenderDisplay(patient.contact.gender)}
            size="small"
            color={getGenderColor(patient.contact.gender)}
          />
        ),
        phone: (
          <MDTypography variant="caption" color="text" fontWeight="regular" sx={rowStyle}>
            {patient.phone_number || "N/A"}
          </MDTypography>
        ),
        doctor: (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <MDTypography variant="caption" color="text" fontWeight="regular" sx={rowStyle}>
              {patient.doctor_info?.doctor_name || "N/A"}
            </MDTypography>
            {canChangeDoctor && (
              <Tooltip title="Change Doctor">
                <IconButton size="small" onClick={() => handleOpenChangeDoctor(patient)} sx={{ p: 0.3 }}>
                  <EditIcon sx={{ fontSize: 14, color: logoColor }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ),
        couple_code: (
          <MDTypography variant="caption" color="text" fontWeight="regular" sx={rowStyle}>
            {patient.couple_code || "N/A"}
          </MDTypography>
        ),
        created_date: (
          <MDTypography variant="caption" color="text" fontWeight="regular" sx={rowStyle}>
            {patient.contact?.created_at ? formatDate(patient.contact.created_at) : "N/A"}
          </MDTypography>
        ),
        actions: (
          <Box display="flex" justifyContent="right">
            <IconButton
              size="small"
              onClick={(event) => handleMenuOpen(event, patient)}
              sx={{
                color: logoColor,
                "&:hover": { backgroundColor: `rgba(0, 121, 142, 0.04)` },
              }}
            >
              <MoreVertIcon />
            </IconButton>
          </Box>
        ),
        customStyles: isPatientMinor ? { backgroundColor: "#fff3e0" } : {}, // For applying to the entire row
      };
    });
    
    return { columns, rows };
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <LoaderOverlay open={loading} />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="logo"
                borderRadius="lg"
                coloredShadow="logo"
              >
                <MDTypography variant="h6" color="white">
                  Patient List
                </MDTypography>
              </MDBox>

              {/* Search Form */}
              <form onSubmit={handleSubmit(handleSearchSubmit)} style={{ padding: "20px" }}>
                <Grid container spacing={2}>
                  {/* First Row */}
                  <Grid item xs={12} md={2.5}>
                    <Controller
                      name="gender"
                      control={control}
                      rules={{ required: "Gender is required" }}
                      render={({ field }) => (
                        <FormControl fullWidth error={!!errors.gender}>
                          <InputLabel id="gender-label">Gender *</InputLabel>
                          <Select
                            {...field}
                            labelId="gender-label"
                            label="Gender *"
                            sx={{ height: selectBoxHeight }}
                          >
                            <MenuItem value="">Select Gender</MenuItem>
                            <MenuItem value="M">Male</MenuItem>
                            <MenuItem value="F">Female</MenuItem>
                            <MenuItem value="O">Other</MenuItem>
                            <MenuItem value="U">Unspecified</MenuItem>
                          </Select>
                          {errors.gender && (
                            <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                              {errors.gender.message}
                            </Typography>
                          )}
                        </FormControl>
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} md={2.5}>
                    <Controller
                      name="country_id"
                      control={control}
                      rules={{ required: "Country is required" }}
                      render={({ field }) => (
                        <FormControl fullWidth error={!!errors.country_id}>
                          <InputLabel id="country-label">Country *</InputLabel>
                          <Select
                            {...field}
                            labelId="country-label"
                            label="Country *"
                            sx={{ height: selectBoxHeight }}
                          >
                            <MenuItem value="">Select Country</MenuItem>
                            {countries.map((country) => (
                              <MenuItem key={country.id} value={country.id}>
                                {country.country_name}
                              </MenuItem>
                            ))}
                          </Select>
                          {errors.country_id && (
                            <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                              {errors.country_id.message}
                            </Typography>
                          )}
                        </FormControl>
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} md={5}>
                    <Controller
                      name="search_text"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Patient Code / Mobile / Couple Code"
                          variant="outlined"
                          fullWidth
                          placeholder="Enter patient code, mobile number, or couple code"
                          helperText="Optional: Search by patient external code, mobile number, or couple code"
                          sx={{ 
                            '& .MuiInputBase-root': { 
                              height: selectBoxHeight 
                            } 
                          }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} md={2}>
                    <Box display="flex" flexDirection="row" gap={1} height="100%">
                      <Button
                        type="submit"
                        variant="contained"
                        startIcon={<SearchIcon />}
                        disabled={loading}
                        fullWidth
                        sx={{
                          padding: "10px 16px",
                          background: "#00798E",
                          color: "#fff",
                          borderRadius: 1,
                          fontSize: "0.95rem",
                          fontWeight: "500",
                          height: selectBoxHeight,
                          '&:hover': {
                            background: "#006375",
                          },
                        }}
                      >
                        Search
                      </Button>
                    </Box>
                  </Grid>

                  {/* Second Row */}
                  <Grid item xs={12} md={2.5}>
                    <Controller
                      name="first_name"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="First Name"
                          variant="outlined"
                          fullWidth
                          placeholder="Enter first name"
                          helperText="Optional: Search by first name"
                          sx={{ 
                            '& .MuiInputBase-root': { 
                              height: selectBoxHeight 
                            } 
                          }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} md={2.5}>
                    <Controller
                      name="last_name"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Last Name"
                          variant="outlined"
                          fullWidth
                          placeholder="Enter last name"
                          helperText="Optional: Search by last name"
                          sx={{ 
                            '& .MuiInputBase-root': { 
                              height: selectBoxHeight 
                            } 
                          }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} md={5} sx={{ display: "flex", alignItems: "center" }}>
                    <Button
                      variant="text"
                      onClick={() => setAdvancedOpen((prev) => !prev)}
                      startIcon={<TuneIcon />}
                      endIcon={advancedOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      sx={{ textTransform: "none", color: "#00798E", fontWeight: 600 }}
                    >
                      Advanced Search
                    </Button>
                  </Grid>

                  <Grid item xs={12} md={2}>
                    <Button
                      variant="outlined"
                      startIcon={<ClearIcon />}
                      onClick={handleClearSearch}
                      fullWidth
                      sx={{
                        borderColor: "#1976d2",
                        color: "#1976d2",
                        height: selectBoxHeight,
                        fontSize: "0.95rem",
                        fontWeight: "500",
                        "&:hover": {
                          borderColor: "#1565c0",
                          backgroundColor: "rgba(25, 118, 210, 0.04)",
                        },
                      }}
                    >
                      Clear
                    </Button>
                  </Grid>
                </Grid>

                <Collapse in={advancedOpen} timeout="auto" unmountOnExit>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="caption" color="textSecondary" sx={{ display: "block", mb: 1, fontWeight: 600 }}>
                    Advanced Search — Registration Date Range &amp; Doctor
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={3}>
                      <Controller
                        name="created_from"
                        control={control}
                        render={({ field }) => (
                          <MDDatePicker
                            label="Registered From"
                            value={field.value || null}
                            onChange={(val) => field.onChange(val || "")}
                            sx={{ '& .MuiOutlinedInput-root': { height: selectBoxHeight } }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Controller
                        name="created_to"
                        control={control}
                        render={({ field }) => (
                          <MDDatePicker
                            label="Registered To"
                            value={field.value || null}
                            onChange={(val) => field.onChange(val || "")}
                            sx={{ '& .MuiOutlinedInput-root': { height: selectBoxHeight } }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Controller
                        name="doctor_id"
                        control={control}
                        render={({ field }) => (
                          <FormControl fullWidth>
                            <InputLabel id="consultant-label">Doctor</InputLabel>
                            <Select
                              {...field}
                              labelId="consultant-label"
                              label="Doctor"
                              sx={{ height: selectBoxHeight }}
                            >
                              <MenuItem value="">All Doctors</MenuItem>
                              {consultants.map((doctor) => (
                                <MenuItem key={doctor.user_id} value={doctor.user_id}>
                                  {doctor.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                      />
                    </Grid>
                  </Grid>
                </Collapse>

                <MDBox mt={2}>
                  <Typography variant="caption" color="textSecondary">
                    * Required fields. Gender and Country must be selected to search.
                  </Typography>
                </MDBox>
              </form>

              {/* Error Message */}
              {error && (
                <MDBox px={3} pb={2}>
                  <Alert severity="error">{error}</Alert>
                </MDBox>
              )}

              {/* Results Section */}
              <MDBox p={3}>
                {!loading && !searchPerformed && (
                  <Box textAlign="center" p={4}>
                    <Typography variant="h6" color="textSecondary">
                      Search for Patients
                    </Typography>
                    <Typography variant="body2" color="textSecondary" mt={1}>
                      Select Gender and Country to search for patients. Patient code/mobile search
                      is optional.
                    </Typography>
                  </Box>
                )}
                {!loading && searchPerformed && patients.length === 0 && !error && (
                  <Box textAlign="center" p={4}>
                    <Typography variant="h6" color="textSecondary">
                      No patients found
                    </Typography>
                    <Typography variant="body2" color="textSecondary" mt={1}>
                      Try adjusting your search criteria or check if the filters are correct.
                    </Typography>
                  </Box>
                )}
                {!loading && patients.length > 0 && (
                  <>
                    {/* Results Header */}
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography variant="h6">
                        Search Results ({pagination.total} patients found)
                      </Typography>
                      <Chip
                        label={`Page ${pagination.page} of ${pagination.pages}`}
                        color="primary"
                        variant="outlined"
                      />
                    </Box>

                    {/* Results Table using DataTable */}
                    <Card>
                      <MDBox pt={3}>
                        <DataTable
                          table={getTableData()}
                          isSorted={false}
                          entriesPerPage={false}
                          showTotalEntries={false}
                          noEndBorder
                          customRowStyles={(row) => row.original.customStyles || {}}
                        />
                      </MDBox>
                    </Card>

                    {/* Actions Dropdown Menu */}
                    <Menu
                      anchorEl={anchorEl}
                      open={Boolean(anchorEl)}
                      onClose={handleMenuClose}
                      PaperProps={{
                        sx: {
                          minWidth: 150,
                          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                        },
                      }}
                    >
                      <MenuItem
                        onClick={() =>
                          handleViewDetails(
                            selectedPatient?.external_code,
                            selectedPatient?.couple_code
                          )
                        }
                        sx={{
                          "&:hover": { backgroundColor: `rgba(0, 121, 142, 0.04)` },
                        }}
                      >
                        <ListItemIcon>
                          <VisibilityIcon fontSize="small" sx={{ color: logoColor }} />
                        </ListItemIcon>
                        <ListItemText primary="View Details" />
                      </MenuItem>
                      <MenuItem
                        onClick={() =>
                          handleEditPatient(
                            selectedPatient?.external_code,
                            selectedPatient?.couple_code
                          )
                        }
                        sx={{
                          "&:hover": { backgroundColor: `rgba(0, 121, 142, 0.04)` },
                        }}
                      >
                        <ListItemIcon>
                          <EditIcon fontSize="small" sx={{ color: logoColor }} />
                        </ListItemIcon>
                        <ListItemText primary="Edit Patient" />
                      </MenuItem>
                      <MenuItem
                        onClick={() =>
                          handleViewHistory(
                            selectedPatient?.external_code,
                            selectedPatient?.couple_code
                          )
                        }
                        sx={{
                          "&:hover": { backgroundColor: `rgba(0, 121, 142, 0.04)` },
                        }}
                      >
                        <ListItemIcon>
                          <HistoryIcon fontSize="small" sx={{ color: logoColor }} />
                        </ListItemIcon>
                        <ListItemText primary="Patient History" />
                      </MenuItem>
                      {canViewAuditLog && (
                        <MenuItem
                          onClick={() => handleViewAuditLog(selectedPatient?.external_code)}
                          sx={{
                            "&:hover": { backgroundColor: `rgba(0, 121, 142, 0.04)` },
                          }}
                        >
                          <ListItemIcon>
                            <TimelineIcon fontSize="small" sx={{ color: logoColor }} />
                          </ListItemIcon>
                          <ListItemText primary="View Audit Log" />
                        </MenuItem>
                      )}
                      <MenuItem
                        onClick={() =>
                          handleViewInvestigations(
                            selectedPatient?.external_code,
                            selectedPatient?.couple_code
                          )
                        }
                        sx={{
                          "&:hover": { backgroundColor: `rgba(0, 121, 142, 0.04)` },
                        }}
                      >
                        <ListItemIcon>
                          <ScienceIcon fontSize="small" sx={{ color: logoColor }} />
                        </ListItemIcon>
                        <ListItemText primary="Manage Investigations" />
                      </MenuItem>
                      <MenuItem
                        onClick={() =>
                          handleViewPrescriptions(
                            selectedPatient?.external_code,
                            selectedPatient?.couple_code
                          )
                        }
                        sx={{
                          "&:hover": { backgroundColor: `rgba(0, 121, 142, 0.04)` },
                        }}
                      >
                        <ListItemIcon>
                          <ListAltIcon fontSize="small" sx={{ color: logoColor }} />
                        </ListItemIcon>
                        <ListItemText primary="View Prescriptions" />
                      </MenuItem>
                      <MenuItem
                        onClick={() =>
                          handleCreateDraftPrescription(
                            selectedPatient?.external_code,
                            selectedPatient?.couple_code
                          )
                        }
                        sx={{
                          "&:hover": { backgroundColor: `rgba(0, 121, 142, 0.04)` },
                        }}
                      >
                        <ListItemIcon>
                          <ReceiptIcon fontSize="small" sx={{ color: logoColor }} />
                        </ListItemIcon>
                        <ListItemText primary="Create Prescription" />
                      </MenuItem>
                      <MenuItem
                        onClick={() =>
                          handleViewInfertilitySummary(
                            selectedPatient?.external_code,
                            selectedPatient?.couple_code
                          )
                        }
                        sx={{
                          "&:hover": { backgroundColor: `rgba(0, 121, 142, 0.04)` },
                        }}
                      >
                        <ListItemIcon>
                          <AssignmentIcon fontSize="small" sx={{ color: logoColor }} />
                        </ListItemIcon>
                        <ListItemText primary="Infertility Summary" />
                      </MenuItem>
                      <MenuItem
                        onClick={() =>
                          handleViewDocuments(
                            selectedPatient?.external_code,
                            selectedPatient?.couple_code
                          )
                        }
                        sx={{
                          "&:hover": { backgroundColor: `rgba(0, 121, 142, 0.04)` },
                        }}
                      >
                        <ListItemIcon>
                          <DescriptionIcon fontSize="small" sx={{ color: logoColor }} />
                        </ListItemIcon>
                        <ListItemText primary="View Documents" />
                      </MenuItem>
                      <MenuItem
                        onClick={() => handleCreateFollowUp(selectedPatient)}
                        sx={{
                          "&:hover": { backgroundColor: `rgba(0, 121, 142, 0.04)` },
                        }}
                      >
                        <ListItemIcon>
                          <EventNoteIcon fontSize="small" sx={{ color: logoColor }} />
                        </ListItemIcon>
                        <ListItemText primary="Create Follow-Up" />
                      </MenuItem>
                      {/* Temporarily disabled — Print ID Card
                      <MenuItem
                        onClick={() => handlePrintCard(selectedPatient)}
                        sx={{
                          "&:hover": { backgroundColor: `rgba(0, 121, 142, 0.04)` },
                        }}
                      >
                        <ListItemIcon>
                          <PrintIcon fontSize="small" sx={{ color: logoColor }} />
                        </ListItemIcon>
                        <ListItemText primary="Print ID Card" />
                      </MenuItem>
                      */}
                      <MenuItem
                        onClick={() => handlePrintPatientCard(selectedPatient)}
                        sx={{
                          "&:hover": { backgroundColor: `rgba(0, 121, 142, 0.04)` },
                        }}
                      >
                        <ListItemIcon>
                          <CreditCardIcon fontSize="small" sx={{ color: logoColor }} />
                        </ListItemIcon>
                        <ListItemText primary="Print Patient Card" />
                      </MenuItem>
                    </Menu>

                    {/* Pagination */}
                    {pagination.pages > 1 && (
                      <Box display="flex" justifyContent="center" mt={3}>
                        <Pagination
                          count={pagination.pages}
                          page={pagination.page}
                          onChange={handlePageChange}
                          color="primary"
                          size="large"
                          showFirstButton
                          showLastButton
                        />
                      </Box>
                    )}
                  </>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />

      {/* Follow-Up creation dialog */}
      <FollowUpDialog
        open={followUpDialogOpen}
        onClose={() => setFollowUpDialogOpen(false)}
        onSuccess={() => {
          setFollowUpDialogOpen(false);
          // Optionally show success feedback
        }}
        contactId={followUpContactId}
        doctors={followUpDoctors}
        defaultDoctorId={followUpDefaultDoctor}
        prescriptionId={null}
        treatmentLine={null}
        currentUserId={currentUserId}
      />

      {/* Change Doctor dialog */}
      <ChangeDoctorDialog
        open={changeDoctorDialogOpen}
        onClose={() => setChangeDoctorDialogOpen(false)}
        patient={changeDoctorPatient}
        onSuccess={handleDoctorChanged}
      />
    </DashboardLayout>
  );
}

export default PatientList;
